import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  Calculator, 
  Home, 
  Wallet,
  TrendingUp,
  PiggyBank,
  FileText,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  Info,
  Calendar,
  PieChart,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { settingsAPI } from '../lib/settings-api';
import {
  calculateTotalTransferCosts,
  calculateTransferCosts,
  DEFAULT_CALCULATOR_CONFIG,
  type PurchaserType,
  type CalculatorConfig,
  type TransferCostResult,
} from '@/config/financial-config';

interface CalculatorsProps {
  onNavigateToPreApproval?: () => void;
}

export function Calculators({ onNavigateToPreApproval }: CalculatorsProps) {
  const [defaultInterestRate, setDefaultInterestRate] = useState('11.75');
  const [defaultLoanTerm, setDefaultLoanTerm] = useState('240');
  const [defaultDepositPercentage, setDefaultDepositPercentage] = useState('20');
  const [defaultTransferDutyRate, setDefaultTransferDutyRate] = useState(11);
  const [defaultBondRegistrationFee, setDefaultBondRegistrationFee] = useState(6000);
  const [defaultDeedsOfficeFee, setDefaultDeedsOfficeFee] = useState(1000);

  // Load settings from backend on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { settings } = await settingsAPI.getSettings();
        const interestRate = settings.economicIndicators?.interestRate?.toString() ?? '11.75';
        const loanTermMonths = ((settings.calculatorDefaults?.loanTerm ?? 20) * 12).toString();
        const depositPct = (settings.calculatorDefaults?.depositPercentage ?? 20).toString();
        const dutyRate = settings.calculatorDefaults?.transferDutyRate ?? 11;
        const bondFee = settings.calculatorDefaults?.bondRegistrationFee ?? 6000;
        const deedsFee = settings.calculatorDefaults?.deedsOfficeFee ?? 1000;

        setDefaultInterestRate(interestRate);
        setDefaultLoanTerm(loanTermMonths);
        setDefaultDepositPercentage(depositPct);
        setDefaultTransferDutyRate(dutyRate);
        setDefaultBondRegistrationFee(bondFee);
        setDefaultDeedsOfficeFee(deedsFee);

        // Transfer Cost Calculator config
        const calcDefaults = settings.calculatorDefaults;
        if (calcDefaults) {
          setCalcConfig({
            vatRate: calcDefaults.vatRate ?? DEFAULT_CALCULATOR_CONFIG.vatRate,
            bankInitFee: calcDefaults.bankInitFee ?? DEFAULT_CALCULATOR_CONFIG.bankInitFee,
            bondDeedsFee: calcDefaults.bondDeedsFee ?? DEFAULT_CALCULATOR_CONFIG.bondDeedsFee,
            transferDeedsFee: calcDefaults.transferDeedsFee ?? DEFAULT_CALCULATOR_CONFIG.transferDeedsFee,
            postFicaFee: calcDefaults.postFicaFee ?? DEFAULT_CALCULATOR_CONFIG.postFicaFee,
          });
        }

        console.log('✅ Calculator defaults loaded from settings');
      } catch (error) {
        console.error('Failed to load calculator settings:', error);
      }
    };

    loadSettings();
  }, []);
  
  // State for Bond Affordability Calculator
  const [affordability, setAffordability] = useState({
    grossMonthlyIncome: '50000',
    totalMonthlyExpenses: '15000',
    interestRate: '11.75',
    repaymentTermMonths: '240',
    allowableDebtRatio: '0.3',
  });
  
  // Update affordability defaults when settings load
  useEffect(() => {
    setAffordability(prev => ({
      ...prev,
      interestRate: defaultInterestRate,
      repaymentTermMonths: defaultLoanTerm
    }));
  }, [defaultInterestRate, defaultLoanTerm]);
  const [affordabilityResults, setAffordabilityResults] = useState<any>(null);

  // State for Bond Repayment Calculator
  const [repayment, setRepayment] = useState({
    loanAmount: '1500000',
    interestRate: '11.75',
    repaymentTermMonths: '240',
  });
  const [repaymentResults, setRepaymentResults] = useState<any>(null);
  
  // Update repayment defaults when settings load
  useEffect(() => {
    setRepayment(prev => ({
      ...prev,
      interestRate: defaultInterestRate,
      repaymentTermMonths: defaultLoanTerm
    }));
  }, [defaultInterestRate, defaultLoanTerm]);

  // State for Transfer Cost Calculator
  const [transferCost, setTransferCost] = useState({
    purchasePrice: '1,000,000',
    bondAmount: '1,000,000',
    purchaserType: 'Natural_person' as PurchaserType,
  });
  const [transferCostResults, setTransferCostResults] = useState<TransferCostResult | null>(null);
  const [transferCostError, setTransferCostError] = useState('');
  const [calcConfig, setCalcConfig] = useState<CalculatorConfig>(DEFAULT_CALCULATOR_CONFIG);

  // State for Extra Payments Calculator
  const [extraPayments, setExtraPayments] = useState({
    loanAmount: '',
    interestRate: '11.75',
    term: '240',
    extraMonthlyPayment: '',
  });
  
  // Update extra payments defaults when settings load
  useEffect(() => {
    setExtraPayments(prev => ({
      ...prev,
      interestRate: defaultInterestRate,
      term: defaultLoanTerm
    }));
  }, [defaultInterestRate, defaultLoanTerm]);
  const [extraPaymentsResults, setExtraPaymentsResults] = useState<any>(null);
  const [showExtraPaymentsAmortization, setShowExtraPaymentsAmortization] = useState(false);

  // State for Deposit Savings Calculator
  const [depositSavings, setDepositSavings] = useState({
    propertyPrice: '',
    depositPercentage: '20',
    monthlySaving: '',
    interestRateOnSavings: '6',
  });
  const [depositSavingsResults, setDepositSavingsResults] = useState<any>(null);

  // Update deposit savings percentage when settings load
  useEffect(() => {
    setDepositSavings(prev => ({ ...prev, depositPercentage: defaultDepositPercentage }));
  }, [defaultDepositPercentage]);

  // State for Amortisation Calculator
  const [amortisation, setAmortisation] = useState({
    loanAmount: '',
    interestRate: '11.75',
    term: '240',
    startDate: new Date().toISOString().split('T')[0],
  });
  const [amortisationResults, setAmortisationResults] = useState<any>(null);

  // Update amortisation defaults when settings load
  useEffect(() => {
    setAmortisation(prev => ({
      ...prev,
      interestRate: defaultInterestRate,
      term: defaultLoanTerm,
    }));
  }, [defaultInterestRate, defaultLoanTerm]);

  // Calculate Bond Affordability
  const calculateAffordability = () => {
    console.log('Affordability State:', affordability);
    const income = parseFloat(affordability.grossMonthlyIncome);
    const expenses = parseFloat(affordability.totalMonthlyExpenses);
    const rate = parseFloat(affordability.interestRate);
    const term = parseInt(affordability.repaymentTermMonths);
    const debtRatio = parseFloat(affordability.allowableDebtRatio);
    console.log('Parsed values:', { income, expenses, rate, term, debtRatio });

    if (isNaN(income) || income <= 0 || isNaN(expenses) || expenses < 0 || isNaN(rate) || rate <= 0 || isNaN(term) || term <= 0) {
      logger.error('Affordability validation failed', new Error('Invalid input'), { income, expenses, rate, term }, 'Calculators');
      toast.error('Please fill in all required fields');
      return;
    }

    const netDisposable = income - expenses;
    const allowedRepayment = Math.min(netDisposable, income * debtRatio);

    const i = rate / 12 / 100;
    const n = term;
    const loanAmount = allowedRepayment * ((1 - Math.pow(1 + i, -n)) / i);

    // Stress testing
    const stressTest1 = allowedRepayment * ((1 - Math.pow(1 + (rate + 1) / 12 / 100, -n)) / ((rate + 1) / 12 / 100));
    const stressTest2 = allowedRepayment * ((1 - Math.pow(1 + (rate + 2) / 12 / 100, -n)) / ((rate + 2) / 12 / 100));

    setAffordabilityResults({
      maximumLoanAmount: loanAmount,
      monthlyRepaymentCapacity: allowedRepayment,
      effectiveDebtToIncomeRatio: (allowedRepayment / income) * 100,
      stressTest_1Percent: stressTest1,
      stressTest_2Percent: stressTest2,
    });

    toast.success('Calculation complete!');
  };

  // Calculate Bond Repayment
  const calculateRepayment = () => {
    console.log('Repayment State:', repayment);
    const loan = parseFloat(repayment.loanAmount);
    const rate = parseFloat(repayment.interestRate);
    const term = parseInt(repayment.repaymentTermMonths);
    console.log('Parsed values:', { loan, rate, term });

    if (isNaN(loan) || loan <= 0 || isNaN(rate) || rate <= 0 || isNaN(term) || term <= 0) {
      logger.error('Repayment validation failed', new Error('Invalid input'), { loan, rate, term }, 'Calculators');
      toast.error('Please fill in all required fields');
      return;
    }

    const i = rate / 12 / 100;
    const n = term;
    const monthlyRepayment = loan * (i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
    const totalPaid = monthlyRepayment * n;
    const totalInterest = totalPaid - loan;

    setRepaymentResults({
      monthlyRepayment,
      totalInterest,
      totalPaid,
    });

    toast.success('Calculation complete!');
  };

  // ─── Transfer Cost Calculator — real-time helpers ──────────────────────────

  const parseFormattedNumber = (val: string): number => {
    return parseFloat(val.replace(/[^\d.]/g, '')) || 0;
  };

  const formatNumberInput = (val: string): string => {
    const plain = val.replace(/,/g, '').replace(/\D/g, '');
    if (plain === '') return '';
    return Number(plain).toLocaleString('en-ZA');
  };

  const formatRand = (val: number): string => {
    return 'R' + Number(val).toLocaleString('en-ZA', { minimumFractionDigits: 0 });
  };

  const handleTransferInput = (field: 'purchasePrice' | 'bondAmount', rawValue: string, inputEl: HTMLInputElement) => {
    const cursorPos = inputEl.selectionStart ?? 0;
    const beforeCursor = rawValue.slice(0, cursorPos);
    const oldCommas = (beforeCursor.match(/,/g) || []).length;
    const formatted = formatNumberInput(rawValue);
    setTransferCost(prev => ({ ...prev, [field]: formatted }));
    requestAnimationFrame(() => {
      const newBeforeCursor = formatted.slice(0, cursorPos);
      const newCommas = (newBeforeCursor.match(/,/g) || []).length;
      const newPos = cursorPos + (newCommas - oldCommas);
      inputEl.setSelectionRange(newPos, newPos);
    });
  };

  // Real-time calculation via useEffect
  useEffect(() => {
    const price = parseFormattedNumber(transferCost.purchasePrice);
    const bond = parseFormattedNumber(transferCost.bondAmount);

    if (price <= 0 || bond <= 0) {
      setTransferCostError(price <= 0 || bond <= 0 ? 'Please enter valid purchase and bond amounts.' : '');
      setTransferCostResults(null);
      return;
    }

    setTransferCostError('');
    const results = calculateTransferCosts(price, bond, transferCost.purchaserType, calcConfig);
    setTransferCostResults(results);
  }, [transferCost.purchasePrice, transferCost.bondAmount, transferCost.purchaserType, calcConfig]);

  // Calculate Extra Payments Impact
  const calculateExtraPayments = () => {
    const loan = parseFloat(extraPayments.loanAmount);
    const rate = parseFloat(extraPayments.interestRate);
    const term = parseInt(extraPayments.term);
    const extra = parseFloat(extraPayments.extraMonthlyPayment) || 0;

    if (!loan || !rate || !term) {
      toast.error('Please fill in all required fields');
      return;
    }

    const i = rate / 12 / 100;
    const n = term;

    // Original calculation
    const originalMonthly = loan * (i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
    const originalTotal = originalMonthly * n;
    const originalInterest = originalTotal - loan;

    // With extra payments - generate amortization schedule
    let balance = loan;
    let month = 0;
    let totalInterestPaid = 0;
    const monthlyPayment = originalMonthly + extra;
    const amortizationSchedule = [];

    while (balance > 0 && month < term) {
      const interestPayment = balance * i;
      const principalPayment = monthlyPayment - interestPayment;
      
      if (principalPayment <= 0) break;
      
      const openingBalance = balance;
      totalInterestPaid += interestPayment;
      balance -= principalPayment;
      month++;
      
      if (balance < 0) balance = 0;

      // Add to amortization schedule (store every month or just yearly for display efficiency)
      amortizationSchedule.push({
        month,
        openingBalance,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        closingBalance: balance,
      });
    }

    const newTermMonths = month;
    const monthsSaved = term - newTermMonths;
    const interestSaved = originalInterest - totalInterestPaid;

    setExtraPaymentsResults({
      originalTermMonths: term,
      newTermMonths,
      monthsSaved,
      originalTotalInterest: originalInterest,
      newTotalInterest: totalInterestPaid,
      interestSaved,
      amortizationSchedule,
    });

    toast.success('Calculation complete!');
  };

  // Calculate Deposit Savings
  const calculateDepositSavings = () => {
    const price = parseFloat(depositSavings.propertyPrice);
    const depositPct = parseFloat(depositSavings.depositPercentage);
    const monthly = parseFloat(depositSavings.monthlySaving);
    const savingsRate = parseFloat(depositSavings.interestRateOnSavings);

    if (!price || !depositPct || !monthly) {
      toast.error('Please fill in all required fields');
      return;
    }

    const depositAmount = price * (depositPct / 100);
    const i = savingsRate / 12 / 100;

    // Solve for n using logarithm
    // FV = monthly * (((1 + i)^n - 1) / i)
    // n = log(1 + (FV * i / monthly)) / log(1 + i)
    
    let monthsRequired;
    if (i === 0) {
      monthsRequired = depositAmount / monthly;
    } else {
      monthsRequired = Math.log(1 + (depositAmount * i / monthly)) / Math.log(1 + i);
    }

    const today = new Date();
    const completionDate = new Date(today.setMonth(today.getMonth() + Math.ceil(monthsRequired)));

    setDepositSavingsResults({
      depositAmount,
      monthsRequired: Math.ceil(monthsRequired),
      estimatedCompletionDate: completionDate.toISOString().split('T')[0],
    });

    toast.success('Calculation complete!');
  };

  // Calculate Amortisation Schedule
  const calculateAmortisation = () => {
    const loan = parseFloat(amortisation.loanAmount);
    const rate = parseFloat(amortisation.interestRate);
    const term = parseInt(amortisation.term);
    const startDate = new Date(amortisation.startDate);

    if (!loan || !rate || !term) {
      toast.error('Please fill in all required fields');
      return;
    }

    const i = rate / 12 / 100;
    const n = term;
    const monthlyPayment = loan * (i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);

    let balance = loan;
    let totalInterest = 0;
    const schedule = [];

    for (let month = 1; month <= n; month++) {
      const interestPayment = balance * i;
      const principalPayment = monthlyPayment - interestPayment;
      const openingBalance = balance;
      balance -= principalPayment;

      if (balance < 0) balance = 0;

      totalInterest += interestPayment;

      const paymentDate = new Date(startDate);
      paymentDate.setMonth(paymentDate.getMonth() + month);

      schedule.push({
        month,
        paymentDate: paymentDate.toISOString().split('T')[0],
        openingBalance,
        interest: interestPayment,
        principal: principalPayment,
        closingBalance: balance,
      });

      if (balance === 0) break;
    }

    setAmortisationResults({
      schedule,
      totalInterest,
      finalPaymentDate: schedule[schedule.length - 1].paymentDate,
    });

    toast.success('Amortisation schedule generated!');
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(253,176,34,0.08),transparent_50%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6"
            >
              <Calculator className="w-4 h-4 text-primary" />
              <span className="text-sm">Mortgage Calculator Suite</span>
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              Financial Calculators
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Accurate, AI-driven calculators to help you make informed property financing decisions. 
              All calculations comply with South African regulations and banking standards.
            </p>
          </motion.div>

          {/* Calculator Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-7xl mx-auto"
          >
            <Tabs defaultValue="affordability" className="w-full">
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-8 h-auto">
                <TabsTrigger value="affordability" data-testid="calculators-affordability-tab" className="flex flex-col items-center gap-2 py-4">
                  <Home className="w-5 h-5" />
                  <span className="text-xs">Affordability</span>
                </TabsTrigger>
                <TabsTrigger value="repayment" data-testid="calculators-repayment-tab" className="flex flex-col items-center gap-2 py-4">
                  <Wallet className="w-5 h-5" />
                  <span className="text-xs">Repayment</span>
                </TabsTrigger>
                <TabsTrigger value="transfer" data-testid="calculators-transfer-tab" className="flex flex-col items-center gap-2 py-4">
                  <FileText className="w-5 h-5" />
                  <span className="text-xs">Transfer Costs</span>
                </TabsTrigger>
                <TabsTrigger value="extra" data-testid="calculators-extra-tab" className="flex flex-col items-center gap-2 py-4">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-xs">Extra Payments</span>
                </TabsTrigger>
                <TabsTrigger value="deposit" data-testid="calculators-deposit-tab" className="flex flex-col items-center gap-2 py-4">
                  <PiggyBank className="w-5 h-5" />
                  <span className="text-xs">Deposit Savings</span>
                </TabsTrigger>
                <TabsTrigger value="amortisation" data-testid="calculators-amortisation-tab" className="flex flex-col items-center gap-2 py-4">
                  <PieChart className="w-5 h-5" />
                  <span className="text-xs">Amortisation</span>
                </TabsTrigger>
              </TabsList>

              {/* Bond Affordability Calculator */}
              <TabsContent value="affordability">
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Home className="w-6 h-6 text-primary" />
                      Bond Affordability Calculator
                    </CardTitle>
                    <CardDescription>
                      Determine the maximum loan amount you qualify for based on your income and expenses
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="aff-income">Gross Monthly Income (R)</Label>
                        <Input
                          id="aff-income"
                          type="number"
                          value={affordability.grossMonthlyIncome}
                          onChange={(e) => setAffordability({ ...affordability, grossMonthlyIncome: e.target.value })}
                          placeholder="50000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="aff-expenses">Total Monthly Expenses (R)</Label>
                        <Input
                          id="aff-expenses"
                          type="number"
                          value={affordability.totalMonthlyExpenses}
                          onChange={(e) => setAffordability({ ...affordability, totalMonthlyExpenses: e.target.value })}
                          placeholder="20000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="aff-rate">Interest Rate (%)</Label>
                        <Input
                          id="aff-rate"
                          type="number"
                          step="0.01"
                          value={affordability.interestRate}
                          onChange={(e) => setAffordability({ ...affordability, interestRate: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="aff-term">Repayment Term</Label>
                        <Select
                          value={affordability.repaymentTermMonths}
                          onValueChange={(value) => setAffordability({ ...affordability, repaymentTermMonths: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="120">10 years</SelectItem>
                            <SelectItem value="180">15 years</SelectItem>
                            <SelectItem value="240">20 years</SelectItem>
                            <SelectItem value="300">25 years</SelectItem>
                            <SelectItem value="360">30 years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button data-testid="calculators-affordability-calculate-button" type="button" onClick={calculateAffordability} className="w-full bg-primary hover:bg-primary/90">
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate Affordability
                    </Button>

                    {affordabilityResults && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4 pt-6 border-t border-border"
                      >
                        <div className="flex items-center gap-2 mb-4">
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          <h3 className="font-semibold">Results</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Card className="border-primary/20 bg-primary/5">
                            <CardHeader className="pb-3">
                              <CardDescription>Maximum Loan Amount</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-3xl font-bold text-primary">
                                R{affordabilityResults.maximumLoanAmount.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-border/50">
                            <CardHeader className="pb-3">
                              <CardDescription>Monthly Repayment Capacity</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-3xl font-bold">
                                R{affordabilityResults.monthlyRepaymentCapacity.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-border/50">
                            <CardHeader className="pb-3">
                              <CardDescription>Debt-to-Income Ratio</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold">
                                {affordabilityResults.effectiveDebtToIncomeRatio.toFixed(1)}%
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-border/50">
                            <CardHeader className="pb-3">
                              <CardDescription>Stress Test (+2%)</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold">
                                R{affordabilityResults.stressTest_2Percent.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                              </p>
                            </CardContent>
                          </Card>
                        </div>

                        <Card className="border-blue-500/20 bg-blue-500/5">
                          <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                              <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                              <div className="text-sm">
                                <p className="font-medium mb-2">Stress Test Results</p>
                                <p className="text-muted-foreground">
                                  At +1% interest: R{affordabilityResults.stressTest_1Percent.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                </p>
                                <p className="text-muted-foreground">
                                  At +2% interest: R{affordabilityResults.stressTest_2Percent.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Pre-Approval CTA */}
                        {onNavigateToPreApproval && (
                          <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 mt-6">
                            <CardContent className="pt-6">
                              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex-1 text-center md:text-left">
                                  <h3 className="font-semibold mb-2">Ready to Make it Official?</h3>
                                  <p className="text-sm text-muted-foreground">
                                    Get your bond pre-approval in minutes and shop with confidence knowing your budget is secured.
                                  </p>
                                </div>
                                <Button 
                                  onClick={onNavigateToPreApproval}
                                  size="lg"
                                  className="bg-primary hover:bg-primary/90 whitespace-nowrap"
                                >
                                  Start Pre-Approval
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Bond Repayment Calculator */}
              <TabsContent value="repayment">
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Wallet className="w-6 h-6 text-primary" />
                      Bond Repayment Calculator
                    </CardTitle>
                    <CardDescription>
                      Calculate your monthly bond repayment and total interest over the loan term
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="rep-loan">Loan Amount (R)</Label>
                        <Input
                          id="rep-loan"
                          type="number"
                          value={repayment.loanAmount}
                          onChange={(e) => setRepayment({ ...repayment, loanAmount: e.target.value })}
                          placeholder="2000000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="rep-rate">Interest Rate (%)</Label>
                        <Input
                          id="rep-rate"
                          type="number"
                          step="0.01"
                          value={repayment.interestRate}
                          onChange={(e) => setRepayment({ ...repayment, interestRate: e.target.value })}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="rep-term">Repayment Term</Label>
                        <Select
                          value={repayment.repaymentTermMonths}
                          onValueChange={(value) => setRepayment({ ...repayment, repaymentTermMonths: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="120">10 years</SelectItem>
                            <SelectItem value="180">15 years</SelectItem>
                            <SelectItem value="240">20 years</SelectItem>
                            <SelectItem value="300">25 years</SelectItem>
                            <SelectItem value="360">30 years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button data-testid="calculators-repayment-calculate-button" type="button" onClick={calculateRepayment} className="w-full bg-primary hover:bg-primary/90">
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate Repayment
                    </Button>

                    {repaymentResults && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4 pt-6 border-t border-border"
                      >
                        <div className="flex items-center gap-2 mb-4">
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          <h3 className="font-semibold">Results</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Card className="border-primary/20 bg-primary/5">
                            <CardHeader className="pb-3">
                              <CardDescription>Monthly Repayment</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-3xl font-bold text-primary">
                                R{repaymentResults.monthlyRepayment.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-border/50">
                            <CardHeader className="pb-3">
                              <CardDescription>Total Interest</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold">
                                R{repaymentResults.totalInterest.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-border/50">
                            <CardHeader className="pb-3">
                              <CardDescription>Total Amount Paid</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold">
                                R{repaymentResults.totalPaid.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                              </p>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Pre-Approval CTA */}
                        {onNavigateToPreApproval && (
                          <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 mt-6">
                            <CardContent className="pt-6">
                              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex-1 text-center md:text-left">
                                  <h3 className="font-semibold mb-2">Know Your Numbers? Get Pre-Approved</h3>
                                  <p className="text-sm text-muted-foreground">
                                    Take the next step and get your bond pre-approval to start house hunting with confidence.
                                  </p>
                                </div>
                                <Button 
                                  onClick={onNavigateToPreApproval}
                                  size="lg"
                                  className="bg-primary hover:bg-primary/90 whitespace-nowrap"
                                >
                                  Start Pre-Approval
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Transfer Cost Calculator */}
              <TabsContent value="transfer">
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-6 h-6 text-primary" />
                      Transfer Cost Calculator
                    </CardTitle>
                    <CardDescription>
                      Calculate all costs associated with transferring property ownership in South Africa
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <Label htmlFor="calc_1_purchase_price">Purchase Price (R)</Label>
                        <Input
                          id="calc_1_purchase_price"
                          data-testid="calculators-transfer-purchase-price"
                          type="text"
                          inputMode="numeric"
                          value={transferCost.purchasePrice}
                          onChange={(e) => handleTransferInput('purchasePrice', e.target.value, e.target)}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pasted = e.clipboardData.getData('text').replace(/[^\d]/g, '');
                            if (pasted) {
                              setTransferCost(prev => ({ ...prev, purchasePrice: formatNumberInput(pasted) }));
                            }
                          }}
                          placeholder="1,000,000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="calc_1_bond_amount">Bond Amount (R)</Label>
                        <Input
                          id="calc_1_bond_amount"
                          data-testid="calculators-transfer-bond-amount"
                          type="text"
                          inputMode="numeric"
                          value={transferCost.bondAmount}
                          onChange={(e) => handleTransferInput('bondAmount', e.target.value, e.target)}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pasted = e.clipboardData.getData('text').replace(/[^\d]/g, '');
                            if (pasted) {
                              setTransferCost(prev => ({ ...prev, bondAmount: formatNumberInput(pasted) }));
                            }
                          }}
                          placeholder="1,000,000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="calc_1_purchaser_status">Status of Purchaser</Label>
                        <Select
                          value={transferCost.purchaserType}
                          onValueChange={(val) => setTransferCost(prev => ({ ...prev, purchaserType: val as PurchaserType }))}
                        >
                          <SelectTrigger id="calc_1_purchaser_status" data-testid="calculators-transfer-purchaser-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Natural_person">Natural Person</SelectItem>
                            <SelectItem value="Company">Company</SelectItem>
                            <SelectItem value="Closed_Corporation">Closed Corporation</SelectItem>
                            <SelectItem value="Trust">Trust</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Validation Error */}
                    {transferCostError && (
                      <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg" id="calc_error">
                        <AlertCircle className="w-4 h-4 text-destructive" />
                        <span className="text-sm text-destructive">{transferCostError}</span>
                      </div>
                    )}

                    {/* Grand Total */}
                    {transferCostResults && (
                      <div className="flex justify-between items-center p-4 bg-primary/10 border border-primary/20 rounded-lg" aria-live="polite" id="calc_1_result">
                        <span className="font-bold text-lg">Total Costs (incl. VAT)</span>
                        <span className="text-2xl font-bold text-primary">
                          {formatRand(transferCostResults.grandTotal)}
                        </span>
                      </div>
                    )}

                    {/* Results — Two Groups */}
                    {transferCostResults && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" aria-live="polite">
                        {/* Bond Registration Costs */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Wallet className="w-5 h-5 text-primary" />
                            <h3 className="font-semibold">Bond Registration Costs</h3>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                            <span className="text-muted-foreground text-sm">Bond Attorney Fee</span>
                            <span className="font-semibold" id="calc_1_result_bond_reg">{formatRand(transferCostResults.bondAttorneyFee)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                            <span className="text-muted-foreground text-sm">Bank Initiation Fee</span>
                            <span className="font-semibold" id="calc_1_result_bank_fee">{formatRand(transferCostResults.bankInitFee)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                            <span className="text-muted-foreground text-sm">Deeds Office Fee</span>
                            <span className="font-semibold" id="calc_1_result_deeds_fees">{formatRand(transferCostResults.bondDeedsFee)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                            <span className="text-muted-foreground text-sm">Post, Petties, FICA & Other</span>
                            <span className="font-semibold" id="calc_1_result_post_petties_fica">{formatRand(transferCostResults.bondPostFica)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-primary/5 border border-primary/10 rounded-lg">
                            <span className="font-semibold text-sm">Bond Registration Total</span>
                            <span className="font-bold text-primary" id="calc_1_result_bond_reg_total">{formatRand(transferCostResults.bondTotal)}</span>
                          </div>
                        </div>

                        {/* Property Transfer Costs */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Home className="w-5 h-5 text-primary" />
                            <h3 className="font-semibold">Property Transfer Costs</h3>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                            <span className="text-muted-foreground text-sm">Transfer Attorney Fee</span>
                            <span className="font-semibold" id="calc_1_result_prop_trans">{formatRand(transferCostResults.transferAttorneyFee)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                            <span className="text-muted-foreground text-sm">Transfer Duty</span>
                            <span className="font-semibold" id="calc_1_result_prop_trans_duty">{formatRand(transferCostResults.transferDuty)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                            <span className="text-muted-foreground text-sm">Deeds Office Fee</span>
                            <span className="font-semibold" id="calc_1_result_prop_deeds_fees">{formatRand(transferCostResults.transferDeedsFee)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                            <span className="text-muted-foreground text-sm">Post, Petties, FICA & Other</span>
                            <span className="font-semibold" id="calc_1_result_prop_post_petties_fica">{formatRand(transferCostResults.transferPostFica)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-primary/5 border border-primary/10 rounded-lg">
                            <span className="font-semibold text-sm">Property Transfer Total</span>
                            <span className="font-bold text-primary" id="calc_1_result_prop_trans_total">{formatRand(transferCostResults.transferTotal)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Disclaimer */}
                    <Card className="border-blue-500/20 bg-blue-500/5">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-muted-foreground space-y-2">
                            <p className="font-medium">Please Note</p>
                            <p>This is a once-off bank fee which you have the option of adding to your bond account. Certain lenders may charge a higher amount for juristic entities, e.g. companies and trusts.</p>
                            <p>If the seller is a VAT vendor, there will be VAT payable on the purchase price instead of transfer duty.</p>
                            <p>Calculators available on this website are only intended to provide an indication of the amounts calculated. Figures generated by the calculators are not binding. Terms and conditions apply.</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pre-Approval CTA */}
                    {onNavigateToPreApproval && (
                      <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5">
                        <CardContent className="pt-6">
                          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex-1 text-center md:text-left">
                              <h3 className="font-semibold mb-2">Ready to Start Your Property Journey?</h3>
                              <p className="text-sm text-muted-foreground">
                                Get pre-approved and know exactly what you can afford, including all associated costs.
                              </p>
                            </div>
                            <Button
                              onClick={onNavigateToPreApproval}
                              size="lg"
                              className="bg-primary hover:bg-primary/90 whitespace-nowrap"
                            >
                              Start Pre-Approval
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Extra Payments Calculator */}
              <TabsContent value="extra">
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-6 h-6 text-primary" />
                      Extra Payments Calculator
                    </CardTitle>
                    <CardDescription>
                      See how extra monthly payments can reduce your loan term and save on interest
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="ep-loan">Loan Amount (R)</Label>
                        <Input
                          id="ep-loan"
                          type="number"
                          value={extraPayments.loanAmount}
                          onChange={(e) => setExtraPayments({ ...extraPayments, loanAmount: e.target.value })}
                          placeholder="2000000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ep-rate">Interest Rate (%)</Label>
                        <Input
                          id="ep-rate"
                          type="number"
                          step="0.01"
                          value={extraPayments.interestRate}
                          onChange={(e) => setExtraPayments({ ...extraPayments, interestRate: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ep-term">Loan Term</Label>
                        <Select
                          value={extraPayments.term}
                          onValueChange={(value) => setExtraPayments({ ...extraPayments, term: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="120">10 years</SelectItem>
                            <SelectItem value="180">15 years</SelectItem>
                            <SelectItem value="240">20 years</SelectItem>
                            <SelectItem value="300">25 years</SelectItem>
                            <SelectItem value="360">30 years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="ep-extra">Extra Monthly Payment (R)</Label>
                        <Input
                          id="ep-extra"
                          type="number"
                          value={extraPayments.extraMonthlyPayment}
                          onChange={(e) => setExtraPayments({ ...extraPayments, extraMonthlyPayment: e.target.value })}
                          placeholder="2000"
                        />
                      </div>
                    </div>

                    <Button data-testid="calculators-extra-calculate-button" onClick={calculateExtraPayments} className="w-full bg-primary hover:bg-primary/90">
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate Savings
                    </Button>

                    {extraPaymentsResults && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4 pt-6 border-t border-border"
                      >
                        <div className="flex items-center gap-2 mb-4">
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          <h3 className="font-semibold">Impact of Extra Payments</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Card className="border-border/50">
                            <CardHeader className="pb-3">
                              <CardDescription>Original Loan Term</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold">
                                {(extraPaymentsResults.originalTermMonths / 12).toFixed(1)} years
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-primary/20 bg-primary/5">
                            <CardHeader className="pb-3">
                              <CardDescription>New Loan Term</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold text-primary">
                                {(extraPaymentsResults.newTermMonths / 12).toFixed(1)} years
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-green-500/20 bg-green-500/5">
                            <CardHeader className="pb-3">
                              <CardDescription>Time Saved</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                {extraPaymentsResults.monthsSaved} months
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-green-500/20 bg-green-500/5">
                            <CardHeader className="pb-3">
                              <CardDescription>Interest Saved</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                R{extraPaymentsResults.interestSaved.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                              </p>
                            </CardContent>
                          </Card>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-muted/30 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Original Total Interest</p>
                            <p className="text-xl font-bold">
                              R{extraPaymentsResults.originalTotalInterest.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                            </p>
                          </div>
                          <div className="p-4 bg-muted/30 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">New Total Interest</p>
                            <p className="text-xl font-bold">
                              R{extraPaymentsResults.newTotalInterest.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                            </p>
                          </div>
                        </div>

                        {/* Show Amortization Button */}
                        <div className="flex justify-center pt-4">
                          <Button
                            data-testid="calculators-extra-amortization-toggle-button"
                            type="button"
                            variant="outline"
                            onClick={() => setShowExtraPaymentsAmortization(!showExtraPaymentsAmortization)}
                            className="border-primary/50 hover:bg-primary/10"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            {showExtraPaymentsAmortization ? 'Hide Amortization' : 'Show Amortization'}
                          </Button>
                        </div>

                        {/* Amortization Schedule Table */}
                        {showExtraPaymentsAmortization && extraPaymentsResults.amortizationSchedule && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-6"
                          >
                            <Card className="border-border/50">
                              <CardHeader>
                                <CardTitle className="text-lg">Amortization Schedule</CardTitle>
                                <CardDescription>
                                  Month-by-month breakdown with extra payments
                                </CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="rounded-md border border-border/50 overflow-hidden">
                                  <div className="max-h-96 overflow-y-auto">
                                    <Table>
                                      <TableHeader className="sticky top-0 bg-muted/50 backdrop-blur-sm">
                                        <TableRow>
                                          <TableHead className="w-20">Month</TableHead>
                                          <TableHead className="text-right">Opening Balance</TableHead>
                                          <TableHead className="text-right">Payment</TableHead>
                                          <TableHead className="text-right">Principal</TableHead>
                                          <TableHead className="text-right">Interest</TableHead>
                                          <TableHead className="text-right">Closing Balance</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {extraPaymentsResults.amortizationSchedule.map((row: any) => (
                                          <TableRow key={row.month} className="hover:bg-muted/30">
                                            <TableCell className="font-medium">{row.month}</TableCell>
                                            <TableCell className="text-right">
                                              R{row.openingBalance.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                            </TableCell>
                                            <TableCell className="text-right">
                                              R{row.payment.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                            </TableCell>
                                            <TableCell className="text-right text-green-600 dark:text-green-400">
                                              R{row.principal.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                            </TableCell>
                                            <TableCell className="text-right text-orange-600 dark:text-orange-400">
                                              R{row.interest.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                              R{row.closingBalance.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                                <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                                  <p className="text-xs text-muted-foreground">
                                    <Info className="w-3 h-3 inline mr-1" />
                                    This schedule shows your loan payoff with the extra monthly payment of R{parseFloat(extraPayments.extraMonthlyPayment || '0').toLocaleString('en-ZA', { maximumFractionDigits: 0 })} included.
                                  </p>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        )}

                        {/* Pre-Approval CTA */}
                        {onNavigateToPreApproval && (
                          <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 mt-6">
                            <CardContent className="pt-6">
                              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex-1 text-center md:text-left">
                                  <h3 className="font-semibold mb-2">Maximize Your Savings Strategy</h3>
                                  <p className="text-sm text-muted-foreground">
                                    Lock in your pre-approval now and start house hunting while you save your deposit.
                                  </p>
                                </div>
                                <Button 
                                  onClick={onNavigateToPreApproval}
                                  size="lg"
                                  className="bg-primary hover:bg-primary/90 whitespace-nowrap"
                                >
                                  Start Pre-Approval
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Deposit Savings Calculator */}
              <TabsContent value="deposit">
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PiggyBank className="w-6 h-6 text-primary" />
                      Deposit Savings Calculator
                    </CardTitle>
                    <CardDescription>
                      Calculate how long it will take to save for your property deposit
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="ds-price">Property Price (R)</Label>
                        <Input
                          id="ds-price"
                          type="number"
                          value={depositSavings.propertyPrice}
                          onChange={(e) => setDepositSavings({ ...depositSavings, propertyPrice: e.target.value })}
                          placeholder="2500000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ds-pct">Deposit Percentage (%)</Label>
                        <Input
                          id="ds-pct"
                          type="number"
                          value={depositSavings.depositPercentage}
                          onChange={(e) => setDepositSavings({ ...depositSavings, depositPercentage: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ds-monthly">Monthly Savings (R)</Label>
                        <Input
                          id="ds-monthly"
                          type="number"
                          value={depositSavings.monthlySaving}
                          onChange={(e) => setDepositSavings({ ...depositSavings, monthlySaving: e.target.value })}
                          placeholder="5000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ds-savingsrate">Interest on Savings (%)</Label>
                        <Input
                          id="ds-savingsrate"
                          type="number"
                          step="0.01"
                          value={depositSavings.interestRateOnSavings}
                          onChange={(e) => setDepositSavings({ ...depositSavings, interestRateOnSavings: e.target.value })}
                        />
                      </div>
                    </div>

                    <Button data-testid="calculators-deposit-calculate-button" onClick={calculateDepositSavings} className="w-full bg-primary hover:bg-primary/90">
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate Savings Timeline
                    </Button>

                    {depositSavingsResults && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4 pt-6 border-t border-border"
                      >
                        <div className="flex items-center gap-2 mb-4">
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          <h3 className="font-semibold">Savings Plan</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Card className="border-primary/20 bg-primary/5">
                            <CardHeader className="pb-3">
                              <CardDescription>Required Deposit</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold text-primary">
                                R{depositSavingsResults.depositAmount.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-border/50">
                            <CardHeader className="pb-3">
                              <CardDescription>Months Required</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold">
                                {depositSavingsResults.monthsRequired} months
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                ({(depositSavingsResults.monthsRequired / 12).toFixed(1)} years)
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-border/50">
                            <CardHeader className="pb-3">
                              <CardDescription>Target Date</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                <p className="font-bold">
                                  {new Date(depositSavingsResults.estimatedCompletionDate).toLocaleDateString('en-ZA', { 
                                    year: 'numeric', 
                                    month: 'long' 
                                  })}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        </div>

                        <Card className="border-blue-500/20 bg-blue-500/5">
                          <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                              <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                              <div className="text-sm text-muted-foreground">
                                <p className="font-medium mb-1">Savings Tips:</p>
                                <ul className="list-disc list-inside space-y-1">
                                  <li>Consider opening a high-interest savings account</li>
                                  <li>Set up automatic monthly transfers to your savings</li>
                                  <li>Look into tax-free savings accounts for better returns</li>
                                </ul>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Pre-Approval CTA */}
                        {onNavigateToPreApproval && (
                          <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 mt-6">
                            <CardContent className="pt-6">
                              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex-1 text-center md:text-left">
                                  <h3 className="font-semibold mb-2">Start Your Journey Today</h3>
                                  <p className="text-sm text-muted-foreground">
                                    Get pre-approved and turn your property dreams into reality with confidence.
                                  </p>
                                </div>
                                <Button 
                                  onClick={onNavigateToPreApproval}
                                  size="lg"
                                  className="bg-primary hover:bg-primary/90 whitespace-nowrap"
                                >
                                  Start Pre-Approval
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Amortisation Calculator */}
              <TabsContent value="amortisation">
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-6 h-6 text-primary" />
                      Bond Amortisation Calculator
                    </CardTitle>
                    <CardDescription>
                      View a detailed breakdown of principal and interest payments over your loan term
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="am-loan">Loan Amount (R)</Label>
                        <Input
                          id="am-loan"
                          type="number"
                          value={amortisation.loanAmount}
                          onChange={(e) => setAmortisation({ ...amortisation, loanAmount: e.target.value })}
                          placeholder="2000000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="am-rate">Interest Rate (%)</Label>
                        <Input
                          id="am-rate"
                          type="number"
                          step="0.01"
                          value={amortisation.interestRate}
                          onChange={(e) => setAmortisation({ ...amortisation, interestRate: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="am-term">Loan Term</Label>
                        <Select
                          value={amortisation.term}
                          onValueChange={(value) => setAmortisation({ ...amortisation, term: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="120">10 years</SelectItem>
                            <SelectItem value="180">15 years</SelectItem>
                            <SelectItem value="240">20 years</SelectItem>
                            <SelectItem value="300">25 years</SelectItem>
                            <SelectItem value="360">30 years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="am-start">Start Date</Label>
                        <Input
                          id="am-start"
                          type="date"
                          value={amortisation.startDate}
                          onChange={(e) => setAmortisation({ ...amortisation, startDate: e.target.value })}
                        />
                      </div>
                    </div>

                    <Button data-testid="calculators-amortisation-calculate-button" onClick={calculateAmortisation} className="w-full bg-primary hover:bg-primary/90">
                      <Calculator className="w-4 h-4 mr-2" />
                      Generate Amortisation Schedule
                    </Button>

                    {amortisationResults && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4 pt-6 border-t border-border"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                            <h3 className="font-semibold">Amortisation Schedule</h3>
                          </div>
                          <Badge variant="outline">
                            {amortisationResults.schedule.length} payments
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <Card className="border-primary/20 bg-primary/5">
                            <CardHeader className="pb-3">
                              <CardDescription>Total Interest Paid</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold text-primary">
                                R{amortisationResults.totalInterest.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                              </p>
                            </CardContent>
                          </Card>

                          <Card className="border-border/50">
                            <CardHeader className="pb-3">
                              <CardDescription>Final Payment Date</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5" />
                                <p className="font-bold">
                                  {new Date(amortisationResults.finalPaymentDate).toLocaleDateString('en-ZA', { 
                                    year: 'numeric', 
                                    month: 'short' 
                                  })}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        </div>

                        <div className="border border-border rounded-lg overflow-hidden">
                          <div className="overflow-x-auto max-h-96 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-muted/50 sticky top-0">
                                <tr>
                                  <th className="px-4 py-3 text-left font-semibold">Month</th>
                                  <th className="px-4 py-3 text-left font-semibold">Date</th>
                                  <th className="px-4 py-3 text-right font-semibold">Opening Balance</th>
                                  <th className="px-4 py-3 text-right font-semibold">Interest</th>
                                  <th className="px-4 py-3 text-right font-semibold">Principal</th>
                                  <th className="px-4 py-3 text-right font-semibold">Closing Balance</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border">
                                {amortisationResults.schedule.slice(0, 60).map((row: any) => (
                                  <tr key={row.month} className="hover:bg-muted/30">
                                    <td className="px-4 py-2">{row.month}</td>
                                    <td className="px-4 py-2">{new Date(row.paymentDate).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short' })}</td>
                                    <td className="px-4 py-2 text-right font-mono">
                                      R{row.openingBalance.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono text-red-600 dark:text-red-400">
                                      R{row.interest.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono text-green-600 dark:text-green-400">
                                      R{row.principal.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono font-semibold">
                                      R{row.closingBalance.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {amortisationResults.schedule.length > 60 && (
                            <div className="p-4 bg-muted/30 text-center text-sm text-muted-foreground border-t border-border">
                              Showing first 60 months of {amortisationResults.schedule.length} total payments
                            </div>
                          )}
                        </div>

                        {/* Pre-Approval CTA */}
                        {onNavigateToPreApproval && (
                          <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 mt-6">
                            <CardContent className="pt-6">
                              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex-1 text-center md:text-left">
                                  <h3 className="font-semibold mb-2">Ready to Take the Next Step?</h3>
                                  <p className="text-sm text-muted-foreground">
                                    Get your bond pre-approval and start your property journey with complete financial clarity.
                                  </p>
                                </div>
                                <Button 
                                  onClick={onNavigateToPreApproval}
                                  size="lg"
                                  className="bg-primary hover:bg-primary/90 whitespace-nowrap"
                                >
                                  Start Pre-Approval
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="w-6 h-6 text-primary" />
                  Important Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <p>
                  <strong>Calculation Standards:</strong> All calculations use nominal annual interest rates compounded monthly, 
                  in accordance with South African banking standards and the National Credit Act.
                </p>
                <p>
                  <strong>Regulatory Compliance:</strong> Transfer duty calculations are based on current SARS tables. 
                  Legal fees are estimated using standard conveyancing tariff tables.
                </p>
                <p>
                  <strong>Accuracy Note:</strong> These calculators provide estimates for planning purposes. 
                  Actual amounts may vary based on your specific circumstances, lender policies, and current market rates.
                </p>
                <p>
                  <strong>Disclaimer:</strong> These tools are for informational purposes only and do not constitute financial advice. 
                  Please consult with a financial advisor or mortgage originator for personalized advice.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
